import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Check, ImagePlus, X, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, LoadingState, ErrorState, StatusBadge, FormError, EmptyState } from "@/components/common";
import { SlotPicker, fmtTime, fmtDayLong } from "@/modules/scheduling/shared";
import { usePortal } from "@/pages/portal/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtMoney, fmtYmd } from "@/lib/format";
import { Car } from "lucide-react";
import { t } from "@/i18n";

const brand = { background: "var(--brand)", color: "var(--brand-contrast)" };
const STEPS = ["vehicle", "service", "details", "time", "summary"];

function Choice({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`w-full rounded-xl border-2 p-4 text-left transition-colors duration-150 ${active ? "border-[var(--brand)] bg-white shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"}`}>{children}</button>
  );
}

export default function PortalBook() {
  const { garage, slug } = usePortal();
  const { me } = useAuth();
  const navigate = useNavigate();
  const tz = garage.timezone;
  const vehicles = useFetch(`/portal/${slug}/vehicles`);
  const services = useFetch(`/portal/${slug}/booking/services`);
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ vehicle: null, service: null, description: "", photos: [], slot: null, own_parts: null, cparts: false });
  const info = useFetch(`/portal/${slug}/booking/info`);
  const [hold, setHold] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef();
  const holdRef = useRef(null);
  holdRef.current = hold;
  useEffect(() => () => { if (holdRef.current) api.post(`/portal/${slug}/booking/hold/${holdRef.current.hold_id}/release`).catch(() => {}); }, [slug]);
  const load = useCallback((day) => api.get(`/portal/${slug}/booking/slots`, { params: { service_id: f.service?.id, day } }).then((r) => r.data), [slug, f.service?.id]);

  if (me === null) return <Navigate to={`/g/${slug}/login`} replace />;
  if (!garage.features?.online_booking) return <Card className="mx-auto max-w-lg text-center" testId="portal-booking-unavailable"><Info className="mx-auto h-6 w-6 text-slate-500" /><p className="mt-3 text-sm text-slate-700">{t("book.unavailable", { garage: garage.name })}</p><Button asChild variant="outline" className="mt-4" data-testid="portal-booking-unavailable-back"><Link to={`/g/${slug}/account`}>{t("portal.myAccount")}</Link></Button></Card>;
  if (vehicles.error) return <ErrorState message={vehicles.error} />;
  if (!vehicles.data || !services.data) return <LoadingState />;

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/portal/${slug}/photos`, fd);
      setF((x) => ({ ...x, photos: [...x.photos, data] }));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      fileRef.current.value = "";
    }
  };
  const toSummary = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/portal/${slug}/booking/hold`, { service_id: f.service.id, vehicle_id: f.vehicle.id, start: f.slot.start, customer_parts_confirmed: f.cparts });
      setHold(data);
      setStep(4);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/portal/${slug}/appointments`, { hold_id: hold.hold_id, description: f.description, photo_ids: f.photos.map((p) => p.id), own_parts: f.own_parts, own_parts_details: f.own_parts ? f.parts_details || "" : "", customer_parts_confirmed: f.cparts });
      setHold(null);
      holdRef.current = null;
      toast.success(data.status === "confirmed" ? t("book.doneConfirmed") : data.status === "awaiting_parts" ? t("b9.parts.portalAwaiting") : t("book.doneRequested"));
      navigate(`/g/${slug}/account?tab=bookings`);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const validation = f.service?.confirmation_mode === "validation";
  const dropoff = f.service?.slot_type === "dropoff";
  const openEnded = dropoff || f.service?.service_kind === "diagnostic";
  const partsCheck = f.service?.parts_confirmation && f.own_parts === false;
  const needCparts = f.service?.customer_parts_required && f.service?.service_kind !== "diagnostic";
  const canNext = [f.vehicle, f.service, f.own_parts !== null && (!needCparts || f.cparts), f.slot][step];
  const periods = info.data?.periods || [];

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="portal-booking">
      <h1 className="text-3xl font-bold text-slate-900">{t("book.title")}</h1>
      <ol className="flex flex-wrap gap-2 text-xs font-semibold">
        {STEPS.map((s, i) => <li key={s} className={`rounded-full px-3 py-1 ${i === step ? "text-white" : i < step ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`} style={i === step ? brand : {}}>{i + 1}. {t(`book.steps.${s}`)}</li>)}
      </ol>
      <FormError message={error} />
      <Card>
        {step === 0 && (vehicles.data.length === 0
          ? <EmptyState icon={Car} title={t("portal.noVehicles")} text={t("book.addVehicleFirst")} action={<Button asChild style={brand} data-testid="book-add-vehicle-link"><Link to={`/g/${slug}/account?tab=vehicles`}>{t("vehicles.add")}</Link></Button>} />
          : <div className="grid gap-3 sm:grid-cols-2">{vehicles.data.map((v) => <Choice key={v.id} active={f.vehicle?.id === v.id} onClick={() => setF({ ...f, vehicle: v })} testId={`book-vehicle-${v.plate}`}><p className="font-mono-plate font-semibold">{v.plate}</p><p className="text-sm text-slate-600">{v.make} {v.model} {v.year || ""}</p></Choice>)}</div>)}
        {step === 1 && (services.data.length === 0 ? <p className="text-sm text-slate-600">{t("book.noServices")}</p> :
          <div className="grid gap-3">{services.data.map((s) => (
            <Choice key={s.id} active={f.service?.id === s.id} onClick={() => setF({ ...f, service: s, slot: null })} testId={`book-service-${s.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold">{s.name}</p>
                <span className="flex gap-1.5"><StatusBadge status={s.slot_type} /><StatusBadge status={s.price_type} /></span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{s.slot_type === "dropoff" ? t("book.dropoffLine") : t("book.workLine", { d: s.duration_min })}{s.price != null ? ` · ${s.price_type === "indicative" ? t("portal.from") : ""}${fmtMoney(s.price, garage.currency)}` : ""}</p>
              <p className="mt-1 text-xs text-slate-500">{s.confirmation_mode === "validation" ? t("book.needsValidation") : t("book.instant")}</p>
            </Choice>))}</div>)}
        {step === 2 && (
          <div className="space-y-4">
            {needCparts && (
              <label className="flex items-start gap-3 rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" data-testid="book-customer-parts">
                <input type="checkbox" className="mt-0.5 h-5 w-5 accent-[var(--brand)]" checked={f.cparts} onChange={(e) => setF({ ...f, cparts: e.target.checked })} data-testid="book-customer-parts-checkbox" />
                <span>{t("b10.cparts.checkbox")}{!f.cparts && <span className="mt-1 block text-xs text-amber-800">{t("b10.cparts.required")}</span>}</span>
              </label>
            )}
            <div className="space-y-2" data-testid="book-own-parts">
              <p className="text-sm font-semibold text-slate-900">{t("b9.parts.question")}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <Choice active={f.own_parts === true} onClick={() => setF({ ...f, own_parts: true })} testId="book-own-parts-yes">{t("b9.parts.yes")}</Choice>
                <Choice active={f.own_parts === false} onClick={() => setF({ ...f, own_parts: false })} testId="book-own-parts-no">{t("b9.parts.no")}</Choice>
              </div>
              {f.own_parts === true && <Textarea rows={2} maxLength={500} value={f.parts_details || ""} placeholder={t("b11.partsDetailsPh")} onChange={(e) => setF({ ...f, parts_details: e.target.value })} data-testid="book-own-parts-details" />}
              {partsCheck && <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="book-parts-note"><Info className="mt-0.5 h-4 w-4 shrink-0" /> {t("b9.parts.needsCheck")}</p>}
            </div>
            <Textarea rows={5} placeholder={t("book.describePlaceholder")} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="book-description" />
            <div className="flex flex-wrap gap-3">
              {f.photos.map((p) => (
                <div key={p.id} className="relative">
                  <img src={fileUrl(p.url)} alt="" className="h-20 w-20 rounded-lg object-cover" />
                  <button type="button" onClick={() => setF({ ...f, photos: f.photos.filter((x) => x.id !== p.id) })} className="absolute -right-2 -top-2 rounded-full bg-white p-0.5 shadow" aria-label={t("common.remove")}><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {f.photos.length < 5 && <Button type="button" variant="outline" disabled={busy} onClick={() => fileRef.current.click()} data-testid="book-add-photo"><ImagePlus className="mr-2 h-4 w-4" /> {busy ? t("common.uploading") : t("book.addPhoto")}</Button>}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="book-photo-input" />
            </div>
            <p className="text-xs text-slate-500">{t("book.photoHint")}</p>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-3">
            {info.data?.restricted && <p className="flex items-start gap-2 rounded-lg bg-slate-100 p-3 text-sm text-slate-800" data-testid="book-periods-note"><Info className="mt-0.5 h-4 w-4 shrink-0" /> {periods.length ? t("b9.periods.portalOpen", { list: periods.map((p) => `${fmtYmd(p.start)} – ${fmtYmd(p.end)}${p.label ? ` (${p.label})` : ""}`).join(", ") }) : t("b9.periods.portalClosed")}</p>}
            {dropoff && <p className="flex items-start gap-2 rounded-lg bg-sky-50 p-3 text-sm text-sky-900" data-testid="book-dropoff-note"><Info className="mt-0.5 h-4 w-4 shrink-0" /> {t("book.dropoffNote")}</p>}
            <SlotPicker load={load} tz={tz} value={f.slot} onChange={(slot) => setF({ ...f, slot })} testPrefix="book-slot" />
          </div>
        )}
        {step === 4 && hold && (
          <div className="space-y-3 text-sm" data-testid="book-summary">
            {[["book.steps.vehicle", `${f.vehicle.plate} · ${f.vehicle.make} ${f.vehicle.model}`], ["book.steps.service", f.service.name],
              ["book.when", `${fmtDayLong(hold.start, tz)} · ${openEnded ? t("b11.dropoffAt", { time: fmtTime(hold.start, tz) }) : `${fmtTime(hold.start, tz)}–${fmtTime(hold.end, tz)}`}`],
              ["book.steps.details", f.description || "—"], ["b9.parts.summary", f.own_parts ? t("common.yes") : t("common.no")], ["book.photos", String(f.photos.length)]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-2"><span className="text-slate-500">{t(k)}</span><span className="text-right font-medium">{v}</span></div>
            ))}
            <p className="rounded-lg bg-amber-50 p-3 text-amber-900" data-testid="book-hold-note">{t("book.heldUntil", { time: fmtTime(hold.expires_at, tz) })}</p>
            <p className="text-slate-600">{partsCheck ? t("b9.parts.needsCheck") : validation ? t("book.validationNote") : t("book.autoNote")}</p>
          </div>
        )}
      </Card>
      <div className="flex justify-between">
        <Button variant="outline" disabled={step === 0 || busy} onClick={() => setStep(step - 1)} data-testid="book-back">{t("book.back")}</Button>
        {step < 3 && <Button disabled={!canNext} onClick={() => setStep(step + 1)} style={brand} data-testid="book-next">{t("book.next")}</Button>}
        {step === 3 && <Button disabled={!f.slot || busy} onClick={toSummary} style={brand} data-testid="book-to-summary">{t("book.review")}</Button>}
        {step === 4 && <Button disabled={busy} onClick={submit} style={brand} data-testid="book-submit"><Check className="mr-2 h-4 w-4" /> {validation || partsCheck ? t("book.sendRequest") : t("book.bookNow")}</Button>}
      </div>
    </div>
  );
}

export function MyBookings({ slug, tz, onChange }) {
  const { data, error, loading, reload } = useFetch(`/portal/${slug}/appointments`);
  const [slotFor, setSlotFor] = useState(null);
  const [slot, setSlot] = useState(null);
  const load = useCallback((day) => api.get(`/portal/${slug}/booking/slots`, { params: { service_id: slotFor?.service_id, day } }).then((r) => r.data), [slug, slotFor]);
  const act = async (path, body, msg) => {
    try {
      await api.post(`/portal/${slug}/appointments/${path}`, body || {});
      toast.success(msg);
      setSlotFor(null);
      reload();
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data.appointments.length) return <p className="text-sm text-slate-500" data-testid="no-bookings">{t("book.noBookings")}</p>;
  return (
    <div className="space-y-3" data-testid="my-bookings">
      <p className="text-xs text-slate-500">{t("book.rules", { h: data.rules.min_notice_hours })}</p>
      {data.appointments.map((a) => (
        <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`booking-${a.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-semibold">{a.service_name} · {a.vehicle}</p>
            <StatusBadge status={a.status} testId={`booking-status-${a.id}`} />
          </div>
          <p className="mt-1 text-sm text-slate-700" data-testid={`booking-when-${a.id}`}>{fmtDayLong(a.start, tz)} · {a.open_ended ? t("b11.dropoffAt", { time: fmtTime(a.start, tz) }) : `${fmtTime(a.start, tz)}–${fmtTime(a.end, tz)}`}</p>
          {a.status === "pending" && <p className="mt-1 text-xs text-amber-800">{t("book.awaiting")}</p>}
          {a.status === "awaiting_parts" && <p className="mt-1 text-xs text-amber-800" data-testid={`booking-parts-note-${a.id}`}>{t("b9.parts.portalAwaiting")}</p>}
          {a.decline_reason && <p className="mt-1 text-xs text-slate-600">{a.decline_reason}</p>}
          {a.proposal && (
            <div className="mt-3 rounded-lg bg-sky-50 p-3 text-sm text-sky-900" data-testid={`booking-proposal-${a.id}`}>
              <p>{t("book.proposalText", { time: `${fmtDayLong(a.proposal.start, tz)} ${fmtTime(a.proposal.start, tz)}` })}</p>
              <div className="mt-2 flex gap-2">
                <Button size="sm" onClick={() => act(`${a.id}/proposal/accept`, null, t("book.proposalAccepted"))} data-testid={`accept-proposal-${a.id}`}>{t("book.accept")}</Button>
                <Button size="sm" variant="outline" onClick={() => act(`${a.id}/proposal/decline`, null, t("book.proposalDeclined"))} data-testid={`decline-proposal-${a.id}`}>{t("book.keepOriginal")}</Button>
              </div>
            </div>
          )}
          {a.status === "awaiting_parts" && <div className="mt-3"><Button size="sm" variant="outline" className="text-red-600" onClick={() => act(`${a.id}/cancel`, null, t("book.cancelled"))} data-testid={`cancel-booking-${a.id}`}>{t("book.cancel")}</Button></div>}
          {["pending", "confirmed"].includes(a.status) && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => { setSlot(null); setSlotFor(slotFor?.id === a.id ? null : { id: a.id, service_id: a.service_id }); }} data-testid={`modify-booking-${a.id}`}>{t("book.modify")}</Button>
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => act(`${a.id}/cancel`, null, t("book.cancelled"))} data-testid={`cancel-booking-${a.id}`}>{t("book.cancel")}</Button>
            </div>
          )}
          {slotFor?.id === a.id && (
            <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
              <SlotPicker load={load} tz={tz} value={slot} onChange={setSlot} testPrefix="modify-slot" />
              <Button size="sm" disabled={!slot} onClick={() => act(`${a.id}/reschedule`, { start: slot.start }, t("book.modified"))} data-testid="modify-confirm">{t("book.confirmNewTime")}</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
